import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Checkbox, Input } from 'antd';
import { AxiosResponse } from 'axios';
import { TbSearch } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';

interface SectorProps {
    filters: any;
    handleChange: any;
}

type FMPSector = {
    sector: string;
};

const Sector: FC<SectorProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasMinimumPlan } = useEntitlements();

    const [sectors, setSectors] = useState<FMPSector[]>([]);

    const handleGetSectors = async () => {
        try {
            setLoading(true);

            await authorizedAxiosInstance.get(`/api/vendors/fmp/sectors`).then((res: AxiosResponse) => {
                if (res?.status === 200) {
                    setSectors(res?.data);

                    setLoading(false);
                }
            });
        } catch (error) {
            captureException(error);

            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            handleGetSectors();
        }
    }, []);

    const [searchText, setSearchText] = useState('');

    // Filter by key or value
    const filteredSectors =
        sectors?.length !== 0
            ? sectors?.filter((key) => key?.sector?.toLowerCase().includes(searchText?.toLowerCase()))
            : [];

    const { token } = useAntDesignTheme();

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <Input
                disabled={loading}
                placeholder='Search...'
                suffix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={searchText}
                onChange={(e: any) => setSearchText(e.target.value)}
            />
            {filteredSectors?.length !== 0 && !loading ? (
                <>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>{filteredSectors?.length} Results</small>
                    </div>
                    <Checkbox.Group
                        value={filters?.sectors}
                        onChange={(checkedValues) => {
                            handleChange(['sectors'], checkedValues);
                        }}
                    >
                        {filteredSectors?.map((e) => {
                            return (
                                <div style={{ marginBottom: '0.125rem', width: '100%' }}>
                                    <Checkbox value={e?.sector} />
                                    <span style={{ marginLeft: '0.5rem' }}>{e?.sector}</span>
                                </div>
                            );
                        })}
                    </Checkbox.Group>
                </>
            ) : (
                loading && (
                    <>
                        <BaseSkeleton title active round paragraph={{ rows: 14 }} style={{ marginTop: '1rem' }} />
                    </>
                )
            )}
        </>
    );
};

export default Sector;
